import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { useDraggable } from '../../hooks/useDraggable';

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResult {
  id: string;
  type: 'dossier' | 'demande' | 'organisation' | 'document';
  title: string;
  subtitle: string;
  link: string;
}

interface QuickLink {
  id: string;
  label: string;
  icon: string;
  link: string;
  color: string;
}

const SEARCH_HISTORY_KEY = 'search_history';
const MAX_HISTORY_ITEMS = 8;

// Accès rapides par rôle
function getQuickLinksForRole(role: string | undefined): QuickLink[] {
  const dashboard: QuickLink = {
    id: 'dashboard',
    label: 'Tableau de bord',
    icon: 'ri-dashboard-line',
    link: '/dashboard',
    color: 'text-teal-600'
  };

  const dossiers: QuickLink = {
    id: 'dossiers',
    label: 'Gestion des Dossiers',
    icon: 'ri-folder-line',
    link: '/dossiers',
    color: 'text-emerald-600'
  };

  const demandes: QuickLink = {
    id: 'demandes',
    label: "Réquisitions",
    icon: 'ri-mail-line',
    link: '/demandes',
    color: 'text-amber-600'
  };

  const documents: QuickLink = {
    id: 'documents',
    label: 'Gestion Documentaire',
    icon: 'ri-file-text-line',
    link: '/gestion-documentaire',
    color: 'text-orange-600'
  };

  const delais72h: QuickLink = {
    id: 'delais-72h',
    label: 'Suivi Délais 72h',
    icon: 'ri-time-line',
    link: '/suivi-delais-72h',
    color: 'text-red-600'
  };

  const notifications: QuickLink = {
    id: 'notifications',
    label: 'Notifications & Alertes',
    icon: 'ri-notification-line',
    link: '/notifications-alertes',
    color: 'text-orange-600'
  };

  const monEspace: QuickLink = {
    id: 'mon-espace',
    label: 'Mon Espace',
    icon: 'ri-user-star-line',
    link: '/mon-espace',
    color: 'text-teal-600'
  };

  const superviseur: QuickLink = {
    id: 'superviseur',
    label: 'Espace Superviseur',
    icon: 'ri-eye-line',
    link: '/superviseur',
    color: 'text-sky-600'
  };

  const espaceEnqueteur: QuickLink = {
    id: 'espace-enqueteur',
    label: 'Espace Enquêteur',
    icon: 'ri-shield-user-line',
    link: '/espace-enqueteur',
    color: 'text-amber-600'
  };

  const espaceCollaborateur: QuickLink = {
    id: 'espace-collaborateur',
    label: 'Espace Collaborateur',
    icon: 'ri-global-line',
    link: '/espace-collaborateur',
    color: 'text-indigo-600'
  };

  const espaceLecteur: QuickLink = {
    id: 'espace-lecteur',
    label: 'Espace Lecteur',
    icon: 'ri-book-read-line',
    link: '/espace-lecteur',
    color: 'text-gray-600'
  };

  const organisations: QuickLink = {
    id: 'organisations',
    label: 'Gestion Organisations',
    icon: 'ri-building-line',
    link: '/gestion-organisations',
    color: 'text-teal-600'
  };

  const utilisateurs: QuickLink = {
    id: 'utilisateurs',
    label: 'Gestion Utilisateurs',
    icon: 'ri-team-line',
    link: '/gestion-utilisateurs',
    color: 'text-violet-600'
  };

  const rapports: QuickLink = {
    id: 'rapports',
    label: 'Rapports Dissémination',
    icon: 'ri-file-chart-line',
    link: '/rapports-dissemination',
    color: 'text-rose-600'
  };

  const conformite: QuickLink = {
    id: 'conformite',
    label: 'Conformité Organisations',
    icon: 'ri-shield-check-line',
    link: '/conformite-organisations',
    color: 'text-green-600'
  };

  const declarations: QuickLink = {
    id: 'declarations',
    label: 'Déclarations Systématiques',
    icon: 'ri-file-list-3-line',
    link: '/declarations-systematiques',
    color: 'text-cyan-600'
  };

  const echanges: QuickLink = {
    id: 'echanges',
    label: "Demande d'information",
    icon: 'ri-exchange-line',
    link: '/echanges-inter-organisations',
    color: 'text-purple-600'
  };

  const monOrganisme: QuickLink = {
    id: 'mon-organisme',
    label: 'Mon Organisme',
    icon: 'ri-building-2-line',
    link: '/mon-organisme',
    color: 'text-teal-600'
  };

  const suiviReponses: QuickLink = {
    id: 'suivi-reponses',
    label: 'Suivi des Réponses',
    icon: 'ri-reply-line',
    link: '/suivi-reponses',
    color: 'text-green-600'
  };

  const reporting: QuickLink = {
    id: 'reporting',
    label: 'Reporting',
    icon: 'ri-bar-chart-line',
    link: '/reporting',
    color: 'text-indigo-600'
  };

  switch (role) {
    case 'admin':
      return [dashboard, dossiers, demandes, utilisateurs, organisations, espaceCollaborateur, echanges, conformite, rapports, notifications];

    case 'superviseur':
      return [dashboard, superviseur, dossiers, demandes, espaceCollaborateur, echanges, rapports, conformite, delais72h, notifications];

    case 'analyste':
      return [dashboard, monEspace, dossiers, demandes, documents, delais72h, notifications, rapports];

    case 'enqueteur':
      return [dashboard, espaceEnqueteur, dossiers, documents, notifications];

    case 'agent_declarant':
      return [dashboard, monOrganisme, declarations, suiviReponses, notifications, demandes];

    case 'collaborateur_national':
    case 'collaborateur_international':
      return [dashboard, espaceCollaborateur, echanges, suiviReponses, monOrganisme, documents, notifications];

    case 'lecteur':
      return [dashboard, espaceLecteur, dossiers, demandes, documents, reporting, notifications];

    default:
      return [dashboard, dossiers, demandes, documents, notifications];
  }
}

export default function GlobalSearchModal({ isOpen, onClose }: GlobalSearchModalProps) {
  const { user } = useAuth();
  const { dragStyle, dragHandleProps } = useDraggable(isOpen);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const quickLinks = getQuickLinksForRole(user?.role);

  // Charger l'historique au montage
  useEffect(() => {
    const history = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (history) {
      setSearchHistory(JSON.parse(history));
    }
  }, []);

  // Sauvegarder l'historique
  const saveToHistory = (term: string) => {
    if (!term.trim()) return;
    
    const updatedHistory = [
      term,
      ...searchHistory.filter(item => item !== term)
    ].slice(0, MAX_HISTORY_ITEMS);
    
    setSearchHistory(updatedHistory);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
  };

  // Supprimer une entrée de l'historique
  const removeFromHistory = (term: string) => {
    const updatedHistory = searchHistory.filter(item => item !== term);
    setSearchHistory(updatedHistory);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
  };

  // Vider tout l'historique
  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  };

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setSearchTerm('');
      setResults([]);
      setSelectedIndex(0);
      setActiveFilter('all');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    setIsSearching(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const term = `%${searchTerm}%`;
        const allResults: SearchResult[] = [];

        // Recherche dans les dossiers
        const { data: dossiers } = await supabase
          .from('dossiers')
          .select('id, reference, description, titre')
          .or(`reference.ilike.${term},description.ilike.${term},titre.ilike.${term}`)
          .limit(8);
        (dossiers ?? []).forEach((d: any) => {
          allResults.push({
            id: d.id,
            type: 'dossier',
            title: d.reference ?? '',
            subtitle: d.titre ?? d.description ?? '',
            link: `/dossiers/${d.id}`,
          });
        });

        // Recherche dans les demandes
        const { data: demandes } = await supabase
          .from('demandes')
          .select('id, reference, objet, type')
          .or(`reference.ilike.${term},objet.ilike.${term}`)
          .limit(8);
        (demandes ?? []).forEach((d: any) => {
          allResults.push({
            id: d.id,
            type: 'demande',
            title: d.reference ?? '',
            subtitle: d.objet ?? d.type ?? '',
            link: `/demandes/${d.id}`,
          });
        });

        // Recherche dans les organisations
        const { data: orgs } = await supabase
          .from('organisations')
          .select('id, nom, code, type')
          .or(`nom.ilike.${term},code.ilike.${term}`)
          .limit(5);
        (orgs ?? []).forEach((o: any) => {
          allResults.push({
            id: o.id,
            type: 'organisation',
            title: o.nom ?? '',
            subtitle: `${o.code ?? ''} - ${o.type ?? ''}`,
            link: `/gestion-organisations?org=${o.id}`,
          });
        });

        // Recherche dans les documents
        const { data: docs } = await supabase
          .from('documents')
          .select('id, nom, categorie')
          .or(`nom.ilike.${term},categorie.ilike.${term}`)
          .limit(5);
        (docs ?? []).forEach((d: any) => {
          allResults.push({
            id: d.id,
            type: 'document',
            title: d.nom ?? '',
            subtitle: d.categorie ?? '',
            link: `/gestion-documentaire?doc=${d.id}`,
          });
        });

        setResults(allResults);
        setSelectedIndex(0);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm]);

  const filteredResults = activeFilter === 'all' 
    ? results 
    : results.filter(r => r.type === activeFilter);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'dossier': return 'ri-folder-line';
      case 'demande': return 'ri-mail-line';
      case 'organisation': return 'ri-building-line';
      case 'document': return 'ri-file-text-line';
      default: return 'ri-search-line';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'dossier': return 'Dossier';
      case 'demande': return 'Demande';
      case 'organisation': return 'Organisation';
      case 'document': return 'Document';
      default: return '';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'dossier': return 'text-emerald-600 bg-emerald-50';
      case 'demande': return 'text-amber-600 bg-amber-50';
      case 'organisation': return 'text-teal-600 bg-teal-50';
      case 'document': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const highlightText = (text: string, term: string) => {
    if (!term) return text;
    const parts = text.split(new RegExp(`(${term})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === term.toLowerCase() 
        ? <mark key={i} className="bg-yellow-200 text-gray-900">{part}</mark>
        : part
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filteredResults[selectedIndex]) {
      e.preventDefault();
      saveToHistory(searchTerm);
      window.REACT_APP_NAVIGATE(filteredResults[selectedIndex].link);
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleResultClick = (result: SearchResult) => {
    saveToHistory(searchTerm);
    window.REACT_APP_NAVIGATE(result.link);
    onClose();
  };

  const handleHistoryClick = (term: string) => {
    setSearchTerm(term);
    inputRef.current?.focus();
  };

  const handleQuickLinkClick = (link: string) => {
    window.REACT_APP_NAVIGATE(link);
    onClose();
  };

  if (!isOpen) return null;

  const typeCounts = {
    dossier: results.filter(r => r.type === 'dossier').length,
    demande: results.filter(r => r.type === 'demande').length,
    organisation: results.filter(r => r.type === 'organisation').length,
    document: results.filter(r => r.type === 'document').length
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20 px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col animate-in fade-in zoom-in-95 duration-200" style={dragStyle}>
            {/* Header */}
            <div className="p-6 border-b border-gray-200" {...dragHandleProps}>
              <div className="flex items-center gap-3">
                <i className="ri-search-line text-xl text-gray-400"></i>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Rechercher des dossiers, demandes, organisations, documents..."
                  className="flex-1 outline-none text-base text-gray-900 placeholder-gray-400"
                />
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors whitespace-nowrap"
                >
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded">Échap</span>
                </button>
              </div>

              {/* Filtres */}
              {searchTerm && results.length > 0 && (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setActiveFilter('all')}
                    className={`px-3 py-1 rounded-full text-sm transition-colors whitespace-nowrap ${
                      activeFilter === 'all'
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Tous ({results.length})
                  </button>
                  {typeCounts.dossier > 0 && (
                    <button
                      onClick={() => setActiveFilter('dossier')}
                      className={`px-3 py-1 rounded-full text-sm transition-colors whitespace-nowrap ${
                        activeFilter === 'dossier'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Dossiers ({typeCounts.dossier})
                    </button>
                  )}
                  {typeCounts.demande > 0 && (
                    <button
                      onClick={() => setActiveFilter('demande')}
                      className={`px-3 py-1 rounded-full text-sm transition-colors whitespace-nowrap ${
                        activeFilter === 'demande'
                          ? 'bg-amber-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Demandes ({typeCounts.demande})
                    </button>
                  )}
                  {typeCounts.organisation > 0 && (
                    <button
                      onClick={() => setActiveFilter('organisation')}
                      className={`px-3 py-1 rounded-full text-sm transition-colors whitespace-nowrap ${
                        activeFilter === 'organisation'
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Organisations ({typeCounts.organisation})
                    </button>
                  )}
                  {typeCounts.document > 0 && (
                    <button
                      onClick={() => setActiveFilter('document')}
                      className={`px-3 py-1 rounded-full text-sm transition-colors whitespace-nowrap ${
                        activeFilter === 'document'
                          ? 'bg-orange-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Documents ({typeCounts.document})
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Contenu */}
            <div className="flex-1 overflow-y-auto p-6">
              {!searchTerm ? (
                <div className="p-4">
                  {/* Historique des recherches */}
                  {searchHistory.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-gray-700">Recherches récentes</h3>
                        <button
                          onClick={clearHistory}
                          className="text-xs text-gray-500 hover:text-red-600 transition-colors whitespace-nowrap"
                        >
                          Tout effacer
                        </button>
                      </div>
                      <div className="space-y-1">
                        {searchHistory.map((term, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 group cursor-pointer"
                          >
                            <i className="ri-history-line text-gray-400 text-sm"></i>
                            <span
                              onClick={() => handleHistoryClick(term)}
                              className="flex-1 text-sm text-gray-700 group-hover:text-teal-600"
                            >
                              {term}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFromHistory(term);
                              }}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-all whitespace-nowrap"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Accès rapides selon le rôle */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Accès rapide</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {quickLinks.map((link) => (
                        <button
                          key={link.id}
                          onClick={() => handleQuickLinkClick(link.link)}
                          className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all group whitespace-nowrap cursor-pointer"
                        >
                          <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50`}>
                            <i className={`${link.icon} text-lg ${link.color}`}></i>
                          </div>
                          <span className="text-sm font-medium text-gray-700 group-hover:text-teal-700">
                            {link.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : isSearching ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mr-3"></div>
                  <span className="text-sm text-gray-500">Recherche en cours...</span>
                </div>
              ) : filteredResults.length > 0 ? (
                <div className="py-2">
                  {filteredResults.map((result, index) => (
                    <button
                      key={result.id}
                      onClick={() => handleResultClick(result)}
                      className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                        index === selectedIndex ? 'bg-teal-50' : ''
                      } whitespace-nowrap`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${getTypeColor(result.type)}`}>
                        <i className={`${getTypeIcon(result.type)} text-base`}></i>
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">
                            {highlightText(result.title, searchTerm)}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeColor(result.type)}`}>
                            {getTypeLabel(result.type)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {highlightText(result.subtitle, searchTerm)}
                        </p>
                      </div>
                      <i className="ri-arrow-right-line text-gray-400 text-sm mt-1"></i>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
                    <i className="ri-search-line text-2xl text-gray-400"></i>
                  </div>
                  <p className="text-sm text-gray-500">Aucun résultat trouvé</p>
                  <p className="text-xs text-gray-400 mt-1">Essayez avec d&apos;autres mots-clés</p>
                </div>
              )}
            </div>

            {/* Footer avec bouton Recherche avancée */}
            {searchTerm && (
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    window.REACT_APP_NAVIGATE(`/recherche-avancee?q=${encodeURIComponent(searchTerm)}`);
                    onClose();
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 cursor-pointer"
                >
                  <i className="ri-search-2-line text-base"></i>
                  <span>Recherche avancée avec plus de filtres</span>
                  <i className="ri-arrow-right-line text-base"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}